/**
 * Unified Task Operations Hook
 * 
 * Simple wrapper around the original working taskStore logic.
 * Provides consistent interface for both Kanban and List views.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSnackbar } from 'notistack';
import useTaskStore from '../stores/taskStore';
import { useSectionOperations } from './useSectionOperations';
// import { useProjectRealtimeSync } from './useProjectRealtimeSync';

export const useUnifiedTaskOperations = (projectId, options = {}) => {
  const { viewType = 'kanban', viewMode = null } = options;
  
  // Initialize realtime sync for this project
  // TEMPORARILY DISABLED to fix infinite loop
  // const { markOperationPending } = useProjectRealtimeSync(projectId || null);
  const markOperationPending = () => {}; // Dummy function
  
  // Use the original working taskStore
  const {
    projectTasks,
    tasks: allTasks,
    myTasks,
    isLoading,
    criticalError,
    fetchProjectSectionsWithTasks,
    fetchTasks,
    fetchMyTasks,
    createTask: storeCreateTask,
    updateTask: storeUpdateTask,
    deleteTask: storeDeleteTask,
    patchTask: storePatchTask,
    repositionTask,
    lastUpdate,
    lastUpdated
  } = useTaskStore();
  
  // Get sections
  const { localSections } = useSectionOperations(projectId);
  
  const { enqueueSnackbar } = useSnackbar();
  
  // Get pagination data from store
  const { sectionPagination, loadMoreTasks } = useTaskStore();

  // Get tasks based on viewMode or projectId
  const tasks = viewMode === 'my' ? myTasks : 
                viewMode === 'all' ? allTasks : 
                projectTasks[projectId] || [];
  
  // Add a state to force recalculation
  const [groupVersion, setGroupVersion] = useState(0);
  
  // Group tasks by section for Kanban view
  const groupedTasks = useMemo(() => {
    console.log('[useUnifiedTaskOperations] Recalculating groupedTasks, version:', groupVersion);
    console.log('[useUnifiedTaskOperations] Current tasks:', tasks.map(t => ({ id: t._id, title: t.title, sectionId: t.sectionId })));
    const grouped = tasks.reduce((acc, task) => {
      // Handle both string and object formats for sectionId
      let sectionKey = null;
      if (task.sectionId) {
        // If sectionId is an object with _id property, use the _id
        if (typeof task.sectionId === 'object' && task.sectionId._id) {
          sectionKey = task.sectionId._id;
        } 
        // If sectionId is a string, use it directly
        else if (typeof task.sectionId === 'string') {
          sectionKey = task.sectionId;
        }
      }
      
      if (!acc[sectionKey]) {
        acc[sectionKey] = [];
      }
      acc[sectionKey].push(task);
      return acc;
    }, {});
    
    console.log('[useUnifiedTaskOperations] Grouped tasks:', Object.keys(grouped).map(key => ({ 
      sectionId: key, 
      taskCount: grouped[key].length,
      taskIds: grouped[key].map(t => t._id)
    })));
    
    // Sort tasks within each section by orderIndex
    // Use a stable sort to prevent unnecessary re-orders
    Object.keys(grouped).forEach(key => {
      grouped[key] = [...grouped[key]].sort((a, b) => {
        const orderDiff = (a.orderIndex || 0) - (b.orderIndex || 0);
        // If orderIndex is the same, sort by ID to ensure stable ordering
        if (orderDiff === 0) {
          return a._id.localeCompare(b._id);
        }
        return orderDiff;
      });
    });
    
    return grouped;
  }, [tasks, groupVersion, lastUpdate, lastUpdated]);
  
  // Load tasks on mount - but only if we don't have data
  useEffect(() => {
    // Check if we already have tasks for this view
    const hasData = viewMode === 'my' ? myTasks.length > 0 :
                   viewMode === 'all' ? allTasks.length > 0 :
                   projectId ? (projectTasks[projectId]?.length > 0) : false;
    
    // Only fetch if we don't have data
    if (!hasData) {
      if (viewMode === 'my') {
        fetchMyTasks();
      } else if (viewMode === 'all') {
        fetchTasks();
      } else if (projectId) {
        fetchProjectSectionsWithTasks(projectId);
      }
    }
  }, [projectId, viewMode]); // Removed function deps to prevent loops

  // ========================================
  // TASK OPERATIONS (using original store methods)
  // ========================================

  const createTask = useCallback(async (taskData) => {
    try {
      const result = await storeCreateTask({ ...taskData, projectId });
      if (result?._id) {
        markOperationPending('task:create', result._id);
      }
      enqueueSnackbar('Task created successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to create task', { variant: 'error' });
      throw error;
    }
  }, [storeCreateTask, projectId, enqueueSnackbar, markOperationPending]);

  const updateTask = useCallback(async (taskId, updates) => {
    try {
      markOperationPending('task:update', taskId);
      await storeUpdateTask(taskId, updates);
      enqueueSnackbar('Task updated successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to update task', { variant: 'error' });
      throw error;
    }
  }, [storeUpdateTask, enqueueSnackbar, markOperationPending]);

  const updateTaskStatus = useCallback(async (taskId, status) => {
    try {
      // Use patchTask for status updates to match MinimalTaskDetails behavior
      await storePatchTask(taskId, { status });
      enqueueSnackbar(`Task marked as ${status === 'done' ? 'complete' : 'incomplete'}`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to update task status', { variant: 'error' });
      throw error;
    }
  }, [storePatchTask, enqueueSnackbar]);

  const assignTask = useCallback(async (taskId, userId) => {
    return updateTask(taskId, { assignedTo: userId });
  }, [updateTask]);

  const updateTaskPriority = useCallback(async (taskId, priority) => {
    return updateTask(taskId, { priority });
  }, [updateTask]);

  const updateTaskDueDate = useCallback(async (taskId, dueDate) => {
    return updateTask(taskId, { dueDate });
  }, [updateTask]);

  const moveTask = useCallback(async (taskId, targetSectionId, position) => {
    console.log('[useUnifiedTaskOperations] moveTask called:', { 
      taskId, 
      targetSectionId, 
      position,
      targetSectionIdType: typeof targetSectionId,
      isNull: targetSectionId === null,
      isUndefined: targetSectionId === undefined
    });
    console.log('[useUnifiedTaskOperations] repositionTask function:', repositionTask);
    
    try {
      if (!repositionTask) {
        console.error('[useUnifiedTaskOperations] repositionTask is not defined!');
        throw new Error('repositionTask is not defined');
      }
      
      // Validate inputs
      if (!taskId) {
        throw new Error('taskId is required');
      }
      if (typeof position !== 'number' || position < 0) {
        throw new Error('position must be a non-negative number');
      }
      
      markOperationPending('task:move', taskId);
      const result = await repositionTask(taskId, targetSectionId, position);
      console.log('[useUnifiedTaskOperations] moveTask result:', result);
      enqueueSnackbar('Task moved successfully', { variant: 'success' });
      return result;
    } catch (error) {
      console.error('[useUnifiedTaskOperations] moveTask error:', error);
      enqueueSnackbar('Failed to move task', { variant: 'error' });
      throw error;
    }
  }, [repositionTask, enqueueSnackbar, markOperationPending]);

  const reorderTasks = useCallback(async (sectionId, taskIds) => {
    console.log('[useUnifiedTaskOperations] reorderTasks called:', { sectionId, taskIds });
    console.trace('[useUnifiedTaskOperations] reorderTasks stack trace');
    
    try {
      // Get the reorderSectionTasks method from store
      const { reorderSectionTasks } = useTaskStore.getState();
      await reorderSectionTasks(sectionId, taskIds);
      enqueueSnackbar('Tasks reordered successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to reorder tasks', { variant: 'error' });
      throw error;
    }
  }, [enqueueSnackbar]);

  const deleteTask = useCallback(async (taskId) => {
    try {
      markOperationPending('task:delete', taskId);
      await storeDeleteTask(taskId);
      enqueueSnackbar('Task deleted successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to delete task', { variant: 'error' });
      throw error;
    }
  }, [storeDeleteTask, enqueueSnackbar, markOperationPending]);

  // ========================================
  // UTILITY FUNCTIONS
  // ========================================

  const getTasksForSection = useCallback((sectionId) => {
    if (viewType === 'kanban') {
      const key = sectionId || null;
      return groupedTasks[key] || [];
    } else {
      return tasks.filter(task => {
        // Handle both string and object formats for sectionId
        let taskSectionId = null;
        if (task.sectionId) {
          if (typeof task.sectionId === 'object' && task.sectionId._id) {
            taskSectionId = task.sectionId._id;
          } else if (typeof task.sectionId === 'string') {
            taskSectionId = task.sectionId;
          }
        }
        return taskSectionId === (sectionId || null);
      });
    }
  }, [viewType, groupedTasks, tasks]);

  const refreshTasks = useCallback(() => {
    console.log('[useUnifiedTaskOperations] refreshTasks called', { viewMode, projectId });
    if (viewMode === 'my') {
      console.log('[useUnifiedTaskOperations] Fetching my tasks');
      return fetchMyTasks();
    } else if (viewMode === 'all') {
      console.log('[useUnifiedTaskOperations] Fetching all tasks');
      return fetchTasks();
    } else if (projectId) {
      console.log('[useUnifiedTaskOperations] Fetching project sections with tasks for:', projectId);
      return fetchProjectSectionsWithTasks(projectId);
    }
    console.log('[useUnifiedTaskOperations] No valid mode, skipping refresh');
    // Return resolved promise if no valid mode
    return Promise.resolve();
  }, [fetchProjectSectionsWithTasks, fetchMyTasks, fetchTasks, projectId, viewMode]);
  
  const forceRefresh = useCallback(() => {
    console.log('[useUnifiedTaskOperations] Force refresh triggered');
    setGroupVersion(prev => prev + 1);
  }, []);

  // ========================================
  // RETURN INTERFACE
  // ========================================

  return {
    // State
    tasks,
    groupedTasks: groupedTasks,
    loading: isLoading,
    error: criticalError,
    operationInProgress: false,
    
    // Sections
    sections: localSections,
    
    // Data access
    getTasksForSection,
    
    // Core operations
    createTask,
    updateTask,
    deleteTask,
    
    // Quick updates
    updateTaskStatus,
    assignTask,
    updateTaskPriority,
    updateTaskDueDate,
    
    // Movement & ordering
    moveTask,
    reorderTasks,
    
    // Nested operations (placeholders)
    addComment: () => {},
    addChecklistItem: () => {},
    toggleChecklistItem: () => {},
    removeChecklistItem: () => {},
    
    // Lifecycle methods
    refreshTasks,
    forceRefresh,
    
    // Pagination methods
    handleLoadMore: useCallback((sectionId) => {
      return loadMoreTasks(projectId, sectionId);
    }, [loadMoreTasks, projectId]),
    
    getRemainingTasks: useCallback((sectionId) => {
      const projectPagination = sectionPagination[projectId] || {};
      // Handle both null and undefined for unsectioned tasks
      const paginationKey = sectionId || null;
      const section = projectPagination[paginationKey] || {};
      const currentCount = getTasksForSection(sectionId).length;
      const totalCount = section.total || 0;
      const remaining = Math.max(0, totalCount - currentCount);
      
      // Debug logging - commented out to reduce noise
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('getRemainingTasks:', {
      //     sectionId,
      //     paginationKey,
      //     section,
      //     currentCount,
      //     totalCount,
      //     remaining,
      //     projectPagination
      //   });
      // }
      
      return remaining;
    }, [sectionPagination, projectId, getTasksForSection]),
    
    getSectionPagination: useCallback((sectionId) => {
      const projectPagination = sectionPagination[projectId] || {};
      // Handle both null and undefined for unsectioned tasks
      const paginationKey = sectionId || null;
      return projectPagination[paginationKey] || { page: 1, hasMore: false, total: 0 };
    }, [sectionPagination, projectId]),
    
    // Status
    hasError: !!criticalError,
    isOperationInProgress: false,
    hasPendingOperations: false
  };
};